<template>
    <div
        id="lt-yantr-v2"
        class="mortgage-rate-table yantr-tracking">
        <div
            v-show="!HideFilters"
            id="inputs"
            class="rate-table-filter">
            <div class="zip-sec">
                <div class="title-block">
                    <h3 v-show="!ShowLocation">
                        <span>{{ LoanProgram }} </span> rates in <span class="geo-location">{{ GeoLocation.city + ', ' + GeoLocation.state }}</span>
                    </h3>
                    <geo-by-zip
                        ref="geoByZip"
                        v-model="GeoLocation"
                        :zip-code="ZipCode"
                        @showHideLocation="showHideLocation()"
                        @input="getGeoDetails"
                    />
                </div>

                <div class="time-stamp-block">
                    <time-stamp v-if="ShowTimeStamp" />
                    <div
                        v-else-if="headerText && headerText.length > 0"
                        class="header-block"
                        v-html="headerText"></div>
                    <div class="adv-disclosure-block">
                        <advertising-disclosures
                            v-if="DisclosureText"
                            :disclosure-text="DisclosureText"
                            :disclosure-tokens="DisclosureTokens"
                            :open-disclosure-modal="openDisclosureModal"
                        />
                        <template v-if="ShowHdltgp">
                            <span>|</span>
                            <button
                                id="ltPaidBtn"
                                class="lt-paid a11y-modal-trigger"
                                data-target="ltPaid">
                                {{ ltPaidTitle }}
                            </button>
                            <div class="dsk-lt-paid">
                                <tooltip-view
                                    :label="ltPaidTitle"
                                    :show-on-over="ShowOnOver"
                                    :popper-content="ltPaidDescription"
                                    reference-element="open me"
                                />
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <mortgage-purchase-rate-table-tabbed
            :credit-band="EstimatedCreditScoreBandId"
            :cta-params="ctaParams"
            :default-loan-program="loanTab"
            :destination-button-text="destinationButtonText"
            :destination-link-or-id="destinationLinkOrId"
            :field-edited="FieldEdited"
            :home-value="EstimatedPropertyValue"
            :loan-amount="LoanAmount"
            :loan-programs="LoanPrograms"
            :loan-type="SelectedLoanType"
            :offer-result="OfferResult"
            :purch-refi="LoanType"
            :show-loading="ShowLoading"
            :show-no-offers="ShowNoOffers"
            :zip-code="ZipCode"
        />
        <a11y-modal
            v-if="ShowHdltgp"
            id="ltPaid">
            <template #h4>
                {{ ltPaidTitle }}
            </template>
            <div slot="body">
                <div class="description">
                    <p> {{ ltPaidDescription }} </p>
                </div>
            </div>
        </a11y-modal>
    </div>
</template>

<script>
    import Accounting from 'accounting';
    import MortgagePurchaseRateTableTabbed from './components/mortgage-purchase-rate-table-tabbed.vue';
    import InteractionTracking from '~/mixin/interaction-tracking';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';
    import GeoByZip from '~/components/geo-by-zip.vue';
    import TimeStamp from '~/components/time-stamp.vue';
    import { getFinancialApiUrl, setHeader } from '~/helpers/financialapi-url-env';
    import GraphQLMixin from '~/mixin/graphql';
    import getMortgagePurchaseOffers from './getMortgagePurchaseOffers.gql';
    import A11yModal from '~/components/a11y-modal.vue';
    import TooltipView from '~/components/tooltip-view.vue';
    import QueryStringHelper from '~/helpers/query-string-helper';
    import GetLocation from '~/mixin/get-geo-location';

    // mixin
    import RateTableMixin from '~/mixin/rate-table';

    export default {
        components: {
            AdvertisingDisclosures,
            MortgagePurchaseRateTableTabbed,
            GeoByZip,
            TimeStamp,
            A11yModal,
            TooltipView,
        },
        mixins: [InteractionTracking, RateTableMixin, GraphQLMixin, GetLocation],
        props: {
            ctaParams: {
                default: '',
            },
            currentMortgageBalance: {
                default: 200000,
            },
            destinationButtonText: {
                default: 'Get Free Offers',
            },
            destinationLinkOrId: {
                default: '',
            },
            disclosureText: {
                default: 'Disclosures',
            },
            disclosureTokens: {
                default: '00001,000127',
            },
            estimatedCreditScoreBandId: {
                default: 'Excellent',
            },
            estimatedDownPayment: {
                default: 50000,
            },
            estimatedPropertyValue: {
                default: 250000,
            },
            graphQlUri: {
                default: '',
            },
            headerText: {
                default: '',
            },
            hideFilters: {
                default: false,
            },
            loanAmount: {
                default: 0,
            },
            loanProgram: {
                default: '',
            },
            loanTab: {
                default: '',
            },
            loanType: {
                default: 'Refinance',
            },
            ltPaidDescription: {
                default: 'LendingTree is compensated by companies on this site and this compensation may impact how and where offers appears on this site (such as the order). LendingTree does not include all lenders, savings products, or loan options available in the marketplace.',
            },
            ltPaidTitle: {
                default: 'How Does LendingTree Get Paid?',
            },
            openDisclosureModal: {
                default: true,
            },
            propertyUse: {
                default: 'Primary Home',
            },
            showLocation: {
                default: true,
            },
            showOnOver: {
                default: true,
            },
            hideHdltgp: {
                default: false,
            },
            showTimeStamp: {
                default: true,
            },
        },
        data() {
            return {
                GeoLocation: {
                    zipCode: this.zipCode,
                },
                LoanType: this.loanType,
                EstimatedPropertyValue: this.estimatedPropertyValue,
                EstimatedDownPayment: this.estimatedDownPayment,
                CurrentMortgageBalance: this.currentMortgageBalance,
                EstimatedCreditScoreBandId: this.estimatedCreditScoreBandId,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                ShowLocation: this.showLocation,
                HideFilters: this.hideFilters,
                ShowTimeStamp: this.showTimeStamp && this.headerText.length === 0,
                PropertyUse: this.propertyUse,
                LoanAmount: this.loanAmount,
                OfferResult: [],
                LoanPrograms: [],
                ZipCode: this.zipCode,
                ShowLoading: false,
                ShowNoOffers: false,
                SelectedLoanType: this.loanType,
                ShowOnOver: this.showOnOver,
                ShowHdltgp: !this.hideHdltgp,
                FieldEdited: false,
                LoanProgram: 'Mortgage Purchase',
            };
        },
        computed: {
            calculatedLoanAmount() {
                if (this.LoanType === 'Refinance') {
                    return Accounting.unformat(this.CurrentMortgageBalance);
                }
                if (this.LoanType === 'Purchase') {
                    return Accounting.unformat(this.EstimatedPropertyValue) - Accounting.unformat(this.EstimatedDownPayment);
                }

                return Accounting.unformat(this.CurrentMortgageBalance);
            },
        },
        watch: {
            LoanType(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.FieldEdited = true;
                }
            },
            EstimatedPropertyValue(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.FieldEdited = true;
                }
            },
            EstimatedDownPayment(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.FieldEdited = true;
                }
            },
            CurrentMortgageBalance(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.FieldEdited = true;
                }
            },
            PropertyUse(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.FieldEdited = true;
                }
            },
            EstimatedCreditScoreBandId(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.FieldEdited = true;
                }
            },
        },
        mounted() {
            this.GraphQlUri = this.graphQlUri !== '' ? this.graphQlUri : getFinancialApiUrl();
            this.createGraphQlClient(this.GraphQlUri, setHeader);
            if (this.loanProgram) {
                const loanProgramLowerCase = this.loanProgram.toLowerCase();
                if (loanProgramLowerCase === 'va' || loanProgramLowerCase === 'fha') {
                    this.LoanProgram = this.loanProgram.toUpperCase();
                }
            }
        },
        created() {
            this.getDetailsFromQueryString();
        },
        methods: {
            async updateRates() {
                this.ShowLoading = true;
                this.ShowNoOffers = false;
                this.SelectedLoanType = this.LoanType;
                this.LoanAmount = this.calculatedLoanAmount;
                const variables = {
                    loanPurpose: this.LoanType,
                    creditBand: this.EstimatedCreditScoreBandId,
                    loanAmount: this.LoanAmount,
                    propertyUse: this.PropertyUse,
                    state: this.GeoLocation.state,
                    loanType: this.getLoanProgram(),
                    downPaymentPercent: this.LoanType === 'Purchase' && Accounting.unformat(this.EstimatedPropertyValue) > 0
                        ? Accounting.unformat(this.EstimatedDownPayment) / Accounting.unformat(this.EstimatedPropertyValue) * 100
                        : 0,
                    ltvPercent: this.LoanType === 'Refinance' && Accounting.unformat(this.EstimatedPropertyValue) > 0
                        ? this.calculatedLoanAmount / Accounting.unformat(this.EstimatedPropertyValue) * 100
                        : 0,
                };
                const data = await this.graphQlQuery(getMortgagePurchaseOffers, variables);
                if (data && data.result) {
                    const { result } = data;
                    this.OfferResult = null;
                    this.LoanPrograms = [];
                    this.$nextTick(() => {
                        if (result && result.length > 0) {
                            const loanPrograms = [...new Set(result.map(item => item.loanProgram))];
                            if (loanPrograms && loanPrograms.length > 0) {
                                const loanPrgramsString = loanPrograms.toString();
                                const tabs = ['15 YR FIXED', '30 YR FIXED'];
                                tabs.forEach((tab) => {
                                    if (loanPrgramsString.toLowerCase().indexOf(tab.toLowerCase()) !== -1) {
                                        this.LoanPrograms.push(tab);
                                    }
                                });
                            }
                        } else {
                            this.ShowNoOffers = true;
                        }
                        this.OfferResult = result;
                    });
                } else {
                    this.$nextTick(() => {
                        this.ShowNoOffers = true;
                        this.LoanPrograms = null;
                        this.OfferResult = null;
                    });
                }
                this.ShowLoading = false;
            },
            showHideLocation() {
                this.ShowLocation = !this.ShowLocation;
            },
            async initializeOffers() {
                await this.updateRates();
            },
            getGeoDetails(location) {
                this.GeoLocation.city = location.city;
                this.GeoLocation.state = location.state;
                this.Zipcode = location.zipCode;
                this.ShowLocation = true;
                this.$nextTick(() => {
                    this.initializeOffers();
                });
            },
            getDetailsFromQueryString() {
                const zipCode = QueryStringHelper.getQueryStringParam('zipcode');
                if (zipCode && zipCode.length > 0) {
                    this.ZipCode = zipCode;
                    GetLocation.methods.GetZipCode(zipCode, this.checkLocationResponse);
                }
            },
            checkLocationResponse(zipDetails) {
                if (!zipDetails.err) {
                    this.$nextTick(() => {
                        this.$refs.geoByZip.updateLocation(zipDetails);
                    });
                }
            },
            getLoanProgram() {
                if (this.loanProgram) {
                    const loanProgramLowerCase = this.loanProgram.toLowerCase();
                    if (loanProgramLowerCase === 'va') {
                        return 'IsVALoan';
                    }
                    if (loanProgramLowerCase === 'fha') {
                        return 'IsFHALoan';
                    }
                }

                return 'Non-VALoan';
            },
        },
    };
</script>
<style src="../../assets/styles/v2/common.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
<style src="../../assets/styles/a11yModal.scss" lang="scss"></style>
